/**
 * Zod semalari — client ve server arasindaki TEK sozlesme.
 *
 * NestJS tarafinda `nestjs-zod` ile ZodValidationPipe'a beslenir; class-validator
 * KULLANILMAZ. Amac: dogrulama kurallarinin iki yerde ayrisamamasi.
 *
 * OFFLINE NOTU: her komut `clientCommandId` tasir. Saha cihazi cevrimdisiyken komutu
 * outbox'a yazar, baglanti gelince gonderir; sunucu ayni id'yi ikinci kez gorurse
 * no-op yapar (idempotency). Bu, "ayni tik iki kere gitti" sinifindaki tum hatalari kapatir.
 */
import { z } from 'zod';
import { ISKELE_TIPLERI } from './measurement.js';
import { IZIN_DURUMLARI, MALZEME_AKIBETLERI, PHOTO_STEPS, REVIZE_TURLERI, ROLES, YAPILAMADI_NEDENLERI, rolEtiket, } from './enums.js';
/** Saha kullanicisi virgullu ondalik giriyor (2,07 / 3,20) — stepper DEGIL, serbest giris. */
const trNumber = z.union([z.number(), z.string()]).transform((v, ctx) => {
    const n = typeof v === 'number' ? v : Number.parseFloat(v.replace(',', '.'));
    if (!Number.isFinite(n)) {
        ctx.addIssue({ code: 'custom', message: 'Geçerli bir sayı girin' });
        return z.NEVER;
    }
    return n;
});
export const uuid = z.uuid();
/** Her yazma komutunun ortak zarfi. */
export const commandEnvelope = z.object({
    /** Istemcide uretilen UUID — idempotency anahtari. */
    clientCommandId: uuid,
    /** Cevrimdisi yakalanan gercek olay zamani. Sunucu saatine GUVENILMEZ (offline). */
    olayZamani: z.coerce.date(),
});
/* ------------------------------------------------------------------ */
/* Olcu (K11 — bir is emri, birden cok iskele)                          */
/* ------------------------------------------------------------------ */
/**
 * ⚠️ ALANLAR TIPE GORE ZORUNLU — tek bir `.refine` ile degil, ACIK kontrolle.
 *    `kapama` satirinda en/boy YOKTUR (daire), `normal` disindakinde yukseklik
 *    ANLAMSIZDIR. Hepsini birden zorunlu tutmak, sahada doldurulamayacak
 *    alanlar uretirdi.
 */
export const olcuSchema = z
    .object({
    ad: z.string().trim().max(80).optional().nullable(),
    tip: z.enum(ISKELE_TIPLERI).default('normal'),
    en: trNumber.pipe(z.number().nonnegative()).optional().nullable(),
    boy: trNumber.pipe(z.number().nonnegative()).optional().nullable(),
    /** Yalniz `normal` tipte anlamli. */
    yuk: trNumber.pipe(z.number().nonnegative()).optional().nullable(),
    /** Yalniz `kapama` tipte anlamli — daire yaricapi (metre). */
    yaricap: trNumber.pipe(z.number().nonnegative()).optional().nullable(),
})
    .superRefine((o, ctx) => {
    const hata = (path, message) => ctx.addIssue({ code: 'custom', path: [path], message });
    if (o.tip === 'kapama') {
        if (!o.yaricap || o.yaricap <= 0)
            hata('yaricap', 'Yarıçap sıfırdan büyük olmalı');
        return;
    }
    if (!o.en || o.en <= 0)
        hata('en', 'En sıfırdan büyük olmalı');
    if (!o.boy || o.boy <= 0)
        hata('boy', 'Boy sıfırdan büyük olmalı');
    if (o.tip === 'normal' && (!o.yuk || o.yuk <= 0)) {
        hata('yuk', 'Normal (hacimli) iskelede yükseklik zorunlu');
    }
});
/* ------------------------------------------------------------------ */
/* Is acma (Bolum 6.2) — her is acma bir KURULUM emridir                */
/* ------------------------------------------------------------------ */
/** SAP siparis formundaki kisa kod alanlari ("STATIK", "MET/Metal"). Bos → NULL. */
const sapAlani = z
    .string()
    .trim()
    .max(40)
    .nullable()
    .optional()
    .transform((v) => (v ? v : v === undefined ? undefined : null));
/**
 * ONCELIK (1-6) — SAP siparis formundaki deger. BILGI ve FILTRE alani.
 * ⚠️ Siralamayi DEGISTIRMEZ (K6, kullanici karari 09.10.2026). Bkz. k6_fifoSirala.
 */
export const oncelikAlani = z.number().int().min(1).max(6).nullable().optional();
export const isAcSchema = commandEnvelope.extend({
    /** K3: kagit yoksa numara da yoktur — bos birakilabilir. */
    isEmriNo: z.string().trim().max(32).optional().nullable(),
    /**
     * "VAR OLAN IS EMRI UZERINE ACIYORUM" — ACIK secim.
     *
     * ⚠️ Bir is emri altinda birden cok iskele olabilir; gercek veride tek
     *    numara altinda 29 iskele var, farkli tarihlerde kurulmus. Ama numarayi
     *    yanlislikla tekrar yazmak da olagan. Ayrim KULLANICIDAN gelir: bayrak
     *    yoksa ve numara kayitliysa sunucu istegi REDDEDER. Mukerrer boylece bir
     *    KAZA degil, SECIM olur.
     *
     * Bayrak ayrica FOTOGRAF DEVRINI tetikler: ayni emirde is emri fotografi
     * varsa yeni ise kopyalanir — kagit tek, 29 kez cektirmek anlamsiz.
     */
    mevcutIsEmri: z.boolean().default(false),
    uniteId: uuid,
    tanim: z.string().trim().min(3, 'İşin tanımı gerekli').max(500),
    /** Coklu: is kapaninca otomatik mail bunlara gider (11 muhatap var). */
    ilgiliKisiIds: z.array(uuid).min(1, 'En az bir ilgili kişi seçin'),
    /** BIRINCI DELEGASYON — "keşfe kim gidecek?". Ayri bir delegasyon adimi YOKTUR. */
    kesifciId: uuid,
    /**
     * SAP IS EMRI ALANLARI — yalniz `sapAlanlari` acik musteride sorulur (SOCAR).
     * Siparis formundaki "Sorumlu Isyeri" ve "Planlama Grubu". Serbest metin.
     */
    sorumluIsyeri: sapAlani,
    planlamaGrubu: sapAlani,
    oncelik: oncelikAlani,
});
/* ------------------------------------------------------------------ */
/* Kesif (Bolum 6.11 — hicbir alan ZORUNLU DEGIL)                       */
/* ------------------------------------------------------------------ */
export const kesifTamamlaSchema = commandEnvelope.extend({
    olculer: z.array(olcuSchema).default([]),
    not: z.string().trim().max(2000).optional().nullable(),
    /** IKINCI DELEGASYON — kurulum ekibi. Kisi sayisi buradan TURETILIR. */
    ekipIds: z.array(uuid).default([]),
    /** v7: zorunluluk kaldirildi, secilmezse yalnizca uyari gosterilir. */
    ekipBasiId: uuid.optional().nullable(),
});
/* ------------------------------------------------------------------ */
/* Izin & malzeme (K4 + K5) — ES ZAMANLI, biri digerini kilitlemez      */
/* ------------------------------------------------------------------ */
export const izinKaydetSchema = commandEnvelope.extend({
    durum: z.enum(IZIN_DURUMLARI),
    /** override secildiyse zorunlu: sorumluluk tiklayandadir, gerekce kayda gecer. */
    overrideNotu: z.string().trim().max(500).optional().nullable(),
});
export const malzemeSevkSchema = commandEnvelope.extend({
    sevkEdildi: z.literal(true),
});
/* ------------------------------------------------------------------ */
/* Kurulum                                                              */
/* ------------------------------------------------------------------ */
export const kurulumBaslaSchema = commandEnvelope.extend({
    /** K5 — izin yoksa bu bayrak olmadan baslatilmaz. */
    overrideOnayi: z.boolean().default(false),
});
/** "Ben Kurdum". Kisi sayisi SORULMAZ — ekipten gelir (v7 karari). */
export const kurulumBittiSchema = commandEnvelope;
/* ------------------------------------------------------------------ */
/* Kontrol & Onay + Teslim — TEK SERT GECIT (K1 + K2 + K3)              */
/* ------------------------------------------------------------------ */
export const teslimSchema = commandEnvelope.extend({
    /**
     * NIHAI OLCULER — kesif olculeri getirilir, sef "aynen kabul et" ya da
     * duzeltir. Sahada son dakika degisebiliyor (balkon eklendi, farkli malzeme).
     *
     * ⚠️ 02.08.2026: tek sayilik `nihaiMetraj` KALDIRILDI. Sef artik tek bir
     *    toplam girmiyor; SATIRLARI duzeltiyor ve iki toplam (m3/m2) otomatik
     *    hesaplaniyor. Satir satir girilmis bir seyi tek sayiya sikistirip sonra
     *    o sayiyi elle duzelttirmek bilgi kaybiydi — hangi parcanin degistigi
     *    kayboluyordu.
     *
     * ⚠️ 05.08'de tek sayilik `nihaiManuel` alani GERI GETIRILDI, 06.08'de tekrar
     *    KALDIRILDI (kullanici karari). Su anki cozum ikisinin de istedigini
     *    veriyor: sef teslimde metraji ELLE belirleyebiliyor ama TOPLAM degil
     *    BOYUT giriyor — hacimliyse en/boy/yuk, asmada en/boy, kapamada yaricap.
     *    Toplam yine buradan hesaplaniyor, dolayisiyla "bu sayi nereden cikti"
     *    sorusu cevapsiz kalmiyor. 02.08 gerekcesi korunmus oluyor.
     *
     *    Yani bu alan tek yoldur; ikinci bir "toplami dogrudan yaz" kapisi YOKTUR.
     */
    nihaiOlculer: z.array(olcuSchema).min(1, 'Nihai ölçü girilmeli'),
    /**
     * ISKELE ONAY NO — musterinin onayla birlikte verdigi numara.
     *
     * ⚠️ 03.08.2026'da IS ACMA adimindan BURAYA tasindi. Numara is acilirken
     *    HENUZ YOKTUR: musteri iskeleyi gorup onaylayinca veriyor. Acilista
     *    sormak, sahada her zaman bos birakilan bir alan uretiyordu.
     *
     * ⚠️ ZORUNLU (musteri karari 03.08.2026). Once "varsa" diye opsiyoneldi;
     *    ama numara TAM DA teslim aninda elde oluyor ve ayni is emri altindaki
     *    iskeleleri birbirinden ayiran TEK alan bu (gercek veride bir is emrinde
     *    29 iskele var). Bos birakilan her teslim, arsivde ayirt edilemeyen bir
     *    satir birakiyordu.
     */
    iskeleOnayNo: z.string().trim().min(1, 'İskele onay no zorunlu').max(32),
});
/** Guvenlik dongusu — is kurulum adimina doner. */
export const guvenliDegilSchema = commandEnvelope.extend({
    gerekce: z.string().trim().min(3, 'Gerekçe girin').max(1000),
});
/* ------------------------------------------------------------------ */
/* Haftalik rutin kontrol (Bolum 6.4) — 7 gunde bir                     */
/* ------------------------------------------------------------------ */
export const rutinKontrolSchema = commandEnvelope.extend({
    sonuc: z.enum(['uygun', 'yesil_kart_kaldirildi']),
    /** "geç" secenegi var: kontrol yapildi ama foto cekilemedi — surtunme azaltma. */
    fotoSayisi: z.number().int().nonnegative().default(0),
    not: z.string().trim().max(1000).optional().nullable(),
    /**
     * Bu kontrolde cekilen fotograflarin numaralari.
     *
     * ⚠️ `fotoSayisi` ile ayni sey DEGIL. Sayac yalnizca "kac tane" der; yesil
     *    kart kaldirma maili ise fotograflarin KENDISINI tasimak zorunda —
     *    musteri "neden kaldirildi"yi gorselden anlar. Sunucu numaralari
     *    dogrular; uydurma numara mail'e giremez.
     */
    fotoNolar: z.array(z.number().int().positive()).max(20).default([]),
}).refine((v) => v.sonuc !== 'yesil_kart_kaldirildi' || (v.not?.trim().length ?? 0) >= 3, {
    /*
     * ⚠️ GEREKCE YALNIZ KALDIRMADA ZORUNLU.
     *
     *    "Her sey yolunda" icin not istemek surtunmedir — haftalik rutin kontrol
     *    zaten en cok atlanan is (Bolum 6.4). Ama kaldirma MUSTERIYE MAIL
     *    URETIYOR ve gerekcesiz bir "iskeleniz kullanilamaz" maili, alan kisiye
     *    ne yapacagini soylemiyor.
     *
     *    Arayuz bunu zaten istiyordu; sunucu ISTEMIYORDU. Ayni kural iki yerde
     *    ayri yasayinca er ya da gec ayrisir — cevrimdisi kuyruktan gelen ya da
     *    eski onbellekli bir istemciden gonderilen komut gerekcesiz gecerdi.
     */
    message: 'Yeşil kart kaldırma gerekçesi zorunlu — müşteriye giden mailde yer alır',
    path: ['not'],
});
/* ------------------------------------------------------------------ */
/* Revize (Bolum 6.6) — kesif YOK, kurulu iskele uzerinde               */
/* ------------------------------------------------------------------ */
export const revizeAcSchema = commandEnvelope.extend({
    tur: z.enum(REVIZE_TURLERI),
    not: z.string().trim().max(1000).optional().nullable(),
    /** Kuran ekip != revize eden ekip olabilir. */
    ekipIds: z.array(uuid).min(1, 'Revize ekibini seçin'),
    ekipBasiId: uuid.optional().nullable(),
});
export const revizeOnaySchema = commandEnvelope.extend({
    /** "Boyut degisti mi?" — degistiyse metraj guncellenir ve iskele tekrar `ayakta` olur. */
    yeniMetraj: trNumber.pipe(z.number().positive()).optional().nullable(),
});
/* ------------------------------------------------------------------ */
/* Sokum (Bolum 6.6, 6.11)                                              */
/* ------------------------------------------------------------------ */
export const sokumAcSchema = commandEnvelope.extend({
    isEmriNo: z.string().trim().max(32).optional().nullable(),
    isEmriFotoVar: z.boolean().default(false),
    /** Kuran ekip != soken ekip olabilir (v7 karari). */
    ekipIds: z.array(uuid).min(1, 'Söküm ekibini seçin'),
    ekipBasiId: uuid.optional().nullable(),
});
/** K12 — hurda YOK. Yalniz iki secenek. */
export const sokumKapatSchema = commandEnvelope.extend({
    malzemeAkibeti: z.enum(MALZEME_AKIBETLERI),
    not: z.string().trim().max(500).optional().nullable(),
});
/* ------------------------------------------------------------------ */
/* Erteleme — is emri arka yuzundeki "yapilmama nedeni" kutulari        */
/* ------------------------------------------------------------------ */
export const erteleSchema = commandEnvelope.extend({
    neden: z.enum(YAPILAMADI_NEDENLERI),
    aciklama: z.string().trim().max(1000).optional().nullable(),
});
/* ------------------------------------------------------------------ */
/* Fotograf (K8) — yetim foto REDDEDILIR                                */
/* ------------------------------------------------------------------ */
export const fotoKaydetSchema = commandEnvelope.extend({
    jobId: uuid,
    step: z.enum(PHOTO_STEPS),
    /** MinIO'ya presigned PUT ile yuklenmis nesnenin anahtari. */
    objectKey: z.string().min(1),
    /** Cihazda cekilme zamani — offline'da sunucu saati YANLISTIR. */
    cekilmeZamani: z.coerce.date(),
    boyutBayt: z.number().int().positive().optional(),
});
/* ------------------------------------------------------------------ */
/* Admin / Tanimlar (K9 — yalniz sef)                                   */
/* ------------------------------------------------------------------ */
export const uniteSchema = z.object({
    ad: z.string().trim().min(1).max(80),
    aktif: z.boolean().default(true),
});
export const ilgiliKisiSchema = z.object({
    ad: z.string().trim().min(1).max(120),
    email: z.email('Geçerli bir e-posta girin'),
    birim: z.string().trim().max(120).optional().nullable(),
    aktif: z.boolean().default(true),
});
export const calisanSchema = z.object({
    ad: z.string().trim().min(1).max(120),
    rol: z.enum(ROLES),
    telefon: z.string().trim().max(32).optional().nullable(),
    aktif: z.boolean().default(true),
});
/* ------------------------------------------------------------------ */
/* Offline senkronizasyon                                               */
/* ------------------------------------------------------------------ */
export const syncPullQuery = z.object({
    /** Son alinan degisiklik imleci. Bos ise ilk tam cekme. */
    since: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(500).default(200),
});
export const syncPushSchema = z.object({
    /** Outbox'taki komutlar SIRAYLA gonderilir; sunucu idempotent uygular. */
    commands: z
        .array(z.object({
        clientCommandId: uuid,
        jobId: uuid.optional().nullable(),
        type: z.string().min(1),
        payload: z.unknown(),
        olayZamani: z.coerce.date(),
    }))
        .max(200),
});
/* ------------------------------------------------------------------ */
/* Gunluk rapor KUNYESI — elle girilen alanlar (SORULAR.md S8)          */
/* ------------------------------------------------------------------ */
/**
 * Gercek gunluk raporda olup sistemin OTO-URETEMEDIGI alanlar.
 *
 * ⚠️ BUNLAR GECICI DEGIL, ARA COZUM DE DEGIL — kalici olabilirler.
 *    S8'in cevabi "otomatiklesecek" cikarsa bile o gun gelene kadar rapor
 *    eksik gidiyordu: ekranda "elle girilecek" yaziyordu ama girilecek yer
 *    yoktu, mailde alanlar hic yoktu. Once ELLE GIRILEBILIR yapiyoruz;
 *    otomatiklesen alan sonra bu yapidan cikarilir.
 *
 * Unite bazinda kontrol adetleri BURADA YOK — o rutin kontrol kayitlarindan
 * turetiliyor (elle girilecek bes alandan biri boylece otomatiklesti).
 */
export const raporKunyeSchema = z.object({
    /** "Sert Rüzgarlı", "Açık", "Yağmurlu" — serbest metin, liste dayatmiyoruz. */
    hava: z.string().trim().max(60).default(''),
    /** Santigrat. Eksi degerler gecerli (Hereke kisin eksiye duser). */
    sicaklik: z.coerce.number().min(-50).max(60).nullable().default(null),
    /** O gun sahada calisan personel sayisi. */
    personel: z.coerce.number().int().min(0).max(999).nullable().default(null),
    /** Binek 1, Hidrolik 2, Kamyonet 1, Forklift 1 — adet sifir olabilir. */
    ekipman: z
        .array(z.object({ ad: z.string().trim().min(1).max(40), adet: z.coerce.number().int().min(0).max(999) }))
        .max(20)
        .default([]),
    /** Sahadaki iskele malzemesi (ton). */
    malzemeTonaj: z.coerce.number().min(0).max(100_000).nullable().default(null),
    /**
     * ONCEKI GUNUN personel listesi — gercek raporda serbest metin.
     *
     * ⚠️ 31.07.2026'da "yarinki personel plani"ndan cevrildi (musteri duzeltmesi).
     *    Rapor gunun SONUNDA/ertesi sabah derleniyor; ileriye donuk bir plan degil,
     *    o gun sahada kimlerin bulundugunun kaydi isteniyor.
     *
     * ⚠️ ESKI ANAHTAR: gonderilmis raporlarin JSON kunyelerinde `yarinkiPersonel`
     *    yaziyor. Arsiv okurken ikisi de kabul edilir (bkz. kunyeOku) — gecmis
     *    rapor MUHURLUDUR, geri donup duzeltilmez (T4'un ruhu).
     */
    dunkuPersonel: z.string().trim().max(2000).default(''),
    /**
     * BUGUN sahada bulunanlar — serbest metin.
     *
     * ⚠️ NEDEN SERBEST METIN: gercek raporda personel listesi ad, gorev ve
     *    giris/cikis saatiyle duruyor; sistemde PUANTAJ VERISI YOK. Yapilandirilmis
     *    bir liste uretmek, olmayan saatleri uydurmak ya da sefe her sabah elle
     *    puantaj girdirmek demekti — ikincisi "artik elle Excel'e girmiyoruz"
     *    vaadine dogrudan ters.
     *
     * ⚠️ BOS GELMEZ: gunun faz ekiplerinden bir ONERI turetilir ve alan dolu
     *    acilir (bkz. `personelOnerisi`). Sef duzeltir; sifirdan yazmaz.
     *
     * ⚠️ ONCEKI GUNDEN TASINMAZ. `dunkuPersonel` gibi bu da her gun degisir;
     *    kopyalanirsa dunun kadrosu bugunun raporuna sessizce girer.
     */
    bugunkuPersonel: z.string().trim().max(2000).default(''),
    /**
     * GUN ICI ONEMLI BILDIRIM VE NOTLAR — Excel sablonunda ayri bir bolum
     * (satir 103) ve gereksinim listesinde "serbest metin ekstra durum/not
     * alani" olarak geciyor.
     *
     * ⚠️ ONCEKI GUNDEN TASINMAZ (KUNYE_TASINAN'da yok). Bir onceki gunun
     *    olayi bugunun raporuna sessizce tasinsaydi musteriye yanlis bilgi
     *    giderdi — hava/sicaklik icin verilen kararin aynisi.
     *
     * ⚠️ BOSSA BOLUM HIC BASILMAZ. Bos bir baslik, doldurulacak bir yer varmis
     *    izlenimi verir; sablonda o kutu bos birakilabiliyor.
     */
    notlar: z.string().trim().max(2000).default(''),
});
export const VARSAYILAN_KUNYE = {
    hava: '',
    sicaklik: null,
    personel: null,
    ekipman: [],
    malzemeTonaj: null,
    dunkuPersonel: '',
    bugunkuPersonel: '',
    notlar: '',
};
/**
 * ONCEKI GUNDEN TASINAN alanlar.
 *
 * Ekipman parki ve malzeme tonaji her gun degismez; sefe her sabah ayni dort
 * satiri yazdirmak "artik elle Excel'e girmiyoruz" vaadine ters duser.
 *
 * ⚠️ Hava, sicaklik ve personel TASINMAZ. Bunlar her gun degisen verilerdir;
 *    kopyalanirlarsa dunun havasi bugunun raporuna SESSIZCE girer ve musteriye
 *    yanlis bilgi gider. Bos gelmeleri kasitlidir — sef doldurmadigini gorur.
 */
export const KUNYE_TASINAN = ['ekipman', 'malzemeTonaj'];
/** Onceki gunun kunyesinden tasinacak alanlari alir, gerisini bos birakir. */
export function kunyeDevret(onceki) {
    if (!onceki)
        return { ...VARSAYILAN_KUNYE };
    const k = { ...VARSAYILAN_KUNYE };
    for (const alan of KUNYE_TASINAN) {
        k[alan] = onceki[alan];
    }
    return k;
}
/** Kunye tamamen bos mu — "rapor eksik gidiyor" uyarisi bunun uzerine kurulu. */
/**
 * BUGUN SAHADA KIMLER VARDI — gunun faz ekiplerinden turetilen ONERI.
 *
 * ⚠️ BU BIR ONERIDIR, KAYIT DEGIL. Sef metni duzeltir; sistemin bildigi tek
 *    sey "bugun hangi fazda kim calisti" ve bu, sahada bulunanlarin tamami
 *    olmayabilir (izinli gelen, malzeme tasiyan, denetime gelen sayilmaz).
 *    Bu yuzden serbest metne YAZILIR, yapilandirilmis bir listeye degil —
 *    yapilandirilmis olsaydi eksikligi gorunmez olurdu.
 *
 * ⚠️ ROLE GORE GRUPLANIR ve rol sirasi sabittir: gercek rapor da once sefi,
 *    sonra teknik kadroyu, en sonra ustalari yaziyor. Alfabetik siralamak
 *    "kim sorumluydu" bilgisini kaybettirirdi.
 */
export function personelOnerisi(kisiler) {
    if (!kisiler.length)
        return '';
    const sira = [
        'sef',
        'proje_muduru',
        'muhendis',
        'isg',
        'idari',
        'formen',
        'usta',
        'operator',
        'depocu',
    ];
    const gruplar = new Map();
    for (const k of kisiler) {
        const l = gruplar.get(k.rol) ?? [];
        if (!l.includes(k.ad))
            l.push(k.ad);
        gruplar.set(k.rol, l);
    }
    return sira
        .filter((r) => gruplar.has(r))
        .map((r) => `${rolEtiket(r)}: ${(gruplar.get(r) ?? []).sort((a, b) => a.localeCompare(b, 'tr')).join(', ')}`)
        .join(' · ');
}
export function kunyeEksikAlanlar(k) {
    const eksik = [];
    if (!k.hava)
        eksik.push('hava');
    if (k.sicaklik == null)
        eksik.push('sicaklik');
    if (k.personel == null)
        eksik.push('personel');
    if (!k.ekipman.length)
        eksik.push('ekipman');
    if (k.malzemeTonaj == null)
        eksik.push('malzemeTonaj');
    if (!k.dunkuPersonel)
        eksik.push('dunkuPersonel');
    if (!k.bugunkuPersonel)
        eksik.push('bugunkuPersonel');
    return eksik;
}
/**
 * Kayitli kunyeyi okur — ESKI ANAHTARI da kabul eder.
 *
 * Gonderilmis raporlarin JSON'unda `yarinkiPersonel` yaziyor. Veritabanindaki
 * gecmis kayitlari toplu guncellemek yerine okurken esliyoruz: gonderilmis
 * rapor MUHURLUDUR, iceriginin sonradan degistirilmemesi gerekir.
 */
export function kunyeOku(ham) {
    const nesne = (ham ?? {});
    const eski = nesne.yarinkiPersonel;
    const girdi = nesne.dunkuPersonel === undefined && typeof eski === 'string'
        ? { ...nesne, dunkuPersonel: eski }
        : nesne;
    const c = raporKunyeSchema.safeParse(girdi);
    return c.success ? c.data : { ...VARSAYILAN_KUNYE };
}
/* ------------------------------------------------------------------ */
/* GECMISE DONUK DUZELTME (Bolum 6.3, adim satirindaki "⋮")             */
/* ------------------------------------------------------------------ */
/**
 * Sefin gecmise donuk duzeltmesi — Faz 7 ihtiyaci.
 *
 * Gerekce (dokumanda birebir): *"eksik is emri kagidi, yanlis izin, karisik
 * foto"*. Saha hizli calisiyor ve yanlis tik atiliyor; duzeltme yolu YOKSA
 * kullanici veriye guvenmeyi birakir ve sistem terk edilir (Bolum 1.4, DIRENC).
 *
 * ⚠️ GEREKCE ZORUNLU. Gerekcesiz bir duzeltme, denetimde HATADAN ayirt
 *    edilemez. T4 iz kaydini degismez yapiyor; bu alan da o kaydin NEDEN
 *    satiridir. Bos birakilamaz.
 *
 * ⚠️ Bu bir "duzenle" degil, EK KAYITTIR. Eski deger silinmez; iz kaydinda
 *    "X → Y" olarak durur (T4 append-only).
 */
export const duzeltmeSchema = commandEnvelope.extend({
    gerekce: z.string().trim().min(3, 'Düzeltme gerekçesi girin — iz kaydına yazılır').max(500),
    /** Is kunyesi. Kagit sonradan bulunursa numarasi da sonradan girilir (K3). */
    isEmriNo: z.string().trim().max(32).nullable().optional(),
    tanim: z.string().trim().min(3).max(500).optional(),
    /**
     * UNITE (iskelenin kuruldugu bolge) — 06.09.2026'da eklendi.
     *
     * ⚠️ NEDEN DUZELTILEBILIR OLMALIYDI (Akcansa kullanici bildirimi): *"iskele
     *    kurulum bolgesini secerken yanlis tuslamisim, firin 1 yerine atik isi
     *    bolgesini secmisim, duzeltme secenegini goremedim."* Kullanici
     *    gozunden kacirmamis; alan duzeltme kapsaminda GERCEKTEN yoktu.
     *
     * ⚠️ SESSIZ DEGIL: unite gunluk raporun sutunlarindan biri ve yesil kart
     *    mailinde de yaziyor. Yanlis kalirsa musteriye giden belgede yanlis
     *    bolge gorunur ve kumulatif "unite basina kontrol" sayilari bozulur —
     *    tam da kesif olculerinin kapali iste bile duzeltilebilir olmasinin
     *    gerekcesi.
     *
     * Kunyenin parcasi oldugu icin `isEmri` alaninin penceresine tabidir:
     * akis surerken evet, is KAPANDIKTAN SONRA hayir.
     */
    uniteId: z.string().uuid().optional(),
    /** SAP is emri alanlari — kunyenin parcasi, `isEmri` penceresine tabi. */
    sorumluIsyeri: sapAlani,
    planlamaGrubu: sapAlani,
    oncelik: oncelikAlani,
    /** Kesif adimi — KAPALI ISTE BILE duzeltilebilir (dokuman ozel istegi). */
    olculer: z.array(olcuSchema).max(50).optional(),
    kesifNot: z.string().trim().max(2000).nullable().optional(),
    /** Izin & malzeme adimi (K4 — degistirmek yalniz sefin yetkisi). */
    izin: z.enum(['alindi', 'override']).nullable().optional(),
    izinNotu: z.string().trim().max(1000).nullable().optional(),
    malzemeSevk: z.boolean().optional(),
    /**
     * Kurulum ekibi — 03.08.2026'da eklendi.
     *
     * ⚠️ K5 ILE CAKISMAZ ama yakinindan gecer: "sorumluluk tiklayanda" der,
     *    ekip listesi ise KIMIN CALISTIGININ kaydidir. Yanlis kisi listede
     *    kalirsa adam-saat ozeti (K7) o kisinin uzerine yazilir. Duzeltme
     *    gerekcesiyle birlikte iz kaydina gecer, tikin sahibi degismez.
     */
    ekip: z.array(z.string().uuid()).max(50).optional(),
    ekipBasiId: z.string().uuid().nullable().optional(),
    /** Kontrol & onay adimi — nihai metraj ve iskele onay no. */
    nihaiOlculer: z.array(olcuSchema).max(50).optional(),
    iskeleOnayNo: z.string().trim().max(32).nullable().optional(),
});
/* ------------------------------------------------------------------ */
/* Hakedis verisi — YALNIZ VERI, HESAP YOK (SOCAR, 09.10.2026)          */
/* ------------------------------------------------------------------ */
export const HAKEDIS_BIRIMLERI = ['m3', 'm2', 'insan_saat'];
/**
 * Soküm talep tarihi, hakedis metraji ve kesinti. Her alan AYRI gonderilir:
 * verilmeyen alana dokunulmaz, `null` alani temizler.
 *
 * ⚠️ BU BIR DUZELTME DEGIL, VERI GIRISIDIR. Hakedis metraji inspektor
 *    kontrolunden gunler sonra gelir; her girisinde gerekce istemek veriyi
 *    girilmez kilardi. Iz kaydi yine "eski → yeni" tutar.
 *
 * ⚠️ HESAP YOK. Devir dokumani §9 hakedisi ayri modul sayiyor; sistem bu
 *    sayilari yalniz saklar ve disa aktarir.
 */
export const hakedisSchema = commandEnvelope.extend({
    /** YYYY-MM-DD — kira hakedisi bu gunden hesaplaniyor. */
    sokumTalepTarihi: z.iso.date().nullable().optional(),
    hakedisMetraj: z.number().nonnegative().max(99_999_999).nullable().optional(),
    hakedisBirim: z.enum(HAKEDIS_BIRIMLERI).nullable().optional(),
    /** Sozlesme geregi iskonto (SOCAR'da 15). */
    kesintiYuzde: z.number().int().min(1).max(99).nullable().optional(),
});
//# sourceMappingURL=schemas.js.map